import React, { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, Checkbox, Divider, Dropdown, Flex, Popover, Tag } from "antd"
import { MoreOutlined, PlusOutlined } from "@ant-design/icons"
import { capitalizeFirstLetter } from "utils/data-formatters"
import { displayPaymentStatus } from "ui/Booking/Details/utils"


const PAYMENT_METHOD_COLOR = {
  card: "blue",
  cash: "green",
  check: "purple",
  bank_transfer: "cyan",
  online: "blue",
  stripe: "blue",
  paypal: "blue"
}

const PAYMENT_STATUS_COLOR = {
  full_paid: "success",
  unpaid: "error",
  partial: "warning",
  pending: "default",
  partial_refund: "orange",
  partially_refunded: "orange",
  refunded: "blue"
}

const BASE_SALES_COLUMN_KEYS = [
  "date",
  "transaction_id",
  "booking_number",
  "displayed_name",
  "payment_method",
  "booking_source",
  "amount",
  "taxes_and_fees",
  "booking_total_amount"
]

const DEFAULT_COLUMN_KEYS = new Set(BASE_SALES_COLUMN_KEYS)

export const formatCurrency = (value) => `$${Number.parseFloat(value || 0).toFixed(2)}`
export const makeOptions = (values) =>
  Array.from(new Set(values.filter(Boolean))).map((value) => ({ label: capitalizeFirstLetter(value), value }))

function baseSalesColumns(t) {
  return [
    {
      title: t("accounting.details.columns.transaction_date"),
      dataIndex: "date",
      key: "date",
      ellipsis: true,
      width: 130,
      render: (d) => d || "-",
      sorter: (a, b) => new Date(a.date || 0) - new Date(b.date || 0)
    },
    {
      title: t("accounting.details.columns.transaction_id"),
      dataIndex: "transaction_id",
      key: "transaction_id",
      ellipsis: true,
      width: 180,
      render: (v, record) => (
        <Flex vertical gap={5}>
          <span>{v || "-"}</span>
          {record.booking_payment_status && (
            <Tag style={{ width: "fit-content", margin: 0 }} color={PAYMENT_STATUS_COLOR[record.booking_payment_status] || "default"}>
              {displayPaymentStatus(record.booking_payment_status)}
            </Tag>
          )}
        </Flex>
      ),
      sorter: (a, b) => (a.transaction_id || "").localeCompare(b.transaction_id || "")
    },
    {
      title: t("accounting.details.columns.booking_id"),
      dataIndex: "booking_number",
      key: "booking_number",
      ellipsis: true,
      width: 110,
      sorter: (a, b) => (a.booking_number || 0) - (b.booking_number || 0)
    },
    {
      title: t("accounting.details.columns.customer_name"),
      dataIndex: "displayed_name",
      key: "displayed_name",
      ellipsis: true,
      width: 160,
      sorter: (a, b) => (a.displayed_name || "").localeCompare(b.displayed_name || "")
    },
    {
      title: t("accounting.details.columns.payment_method"),
      dataIndex: "payment_method",
      key: "payment_method",
      ellipsis: true,
      width: 200,
      render: (v) => v ? <Tag color={PAYMENT_METHOD_COLOR[v?.toLowerCase()] || "default"} style={{ margin: 0 }}>{capitalizeFirstLetter(v)}</Tag> : "-",
      sorter: (a, b) => (a.payment_method || "").localeCompare(b.payment_method || "")
    },
    {
      title: t("accounting.details.columns.booking_source"),
      dataIndex: "booking_source",
      key: "booking_source",
      ellipsis: true,
      width: 150,
      render: (v) => capitalizeFirstLetter(v) || "-",
      sorter: (a, b) => (a.booking_source || "").localeCompare(b.booking_source || "")
    },
    {
      title: t("accounting.details.columns.payment_amount"),
      dataIndex: "amount",
      key: "amount",
      ellipsis: true,
      width: 140,
      render: (_, record) => record.formatted_amount || formatCurrency(record.amount),
      align: "right",
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0)
    },
    {
      title: t("accounting.details.columns.taxes_and_fees"),
      dataIndex: "taxes_and_fees",
      key: "taxes_and_fees",
      ellipsis: true,
      width: 140,
      render: (v) => formatCurrency(v),
      align: "right",
      sorter: (a, b) => Number(a.taxes_and_fees || 0) - Number(b.taxes_and_fees || 0)
    },
    {
      title: t("accounting.details.columns.total_amount"),
      dataIndex: "booking_total_amount",
      key: "booking_total_amount",
      ellipsis: true,
      width: 130,
      render: (v) => formatCurrency(v),
      align: "right",
      sorter: (a, b) => Number(a.booking_total_amount || 0) - Number(b.booking_total_amount || 0)
    }
  ]
}

function optionalSalesColumns(t) {
  return [
    {
      title: t("accounting.details.columns.base_cost"),
      dataIndex: "base_cost",
      key: "base_cost",
      ellipsis: true,
      width: 120,
      render: (v) => formatCurrency(v),
      align: "right",
      sorter: (a, b) => Number(a.base_cost || 0) - Number(b.base_cost || 0)
    },
    {
      title: t("accounting.details.columns.first_experience_date"),
      dataIndex: "first_experience_date",
      key: "first_experience_date",
      ellipsis: true,
      width: 170,
      render: (d) => d || "-",
      sorter: (a, b) => new Date(a.first_experience_date || 0) - new Date(b.first_experience_date || 0)
    },
    {
      title: t("accounting.details.columns.last_experience_date"),
      dataIndex: "last_experience_date",
      key: "last_experience_date",
      ellipsis: true,
      width: 160,
      render: (d) => d || "-",
      sorter: (a, b) => new Date(a.last_experience_date || 0) - new Date(b.last_experience_date || 0)
    }
  ]
}

export function useDetailsColumns({ isSales, onEdit, onDelete }) {
  const { t } = useTranslation()
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() => new Set(BASE_SALES_COLUMN_KEYS))

  const toggleColumn = useCallback((key, checked) => {
    if (DEFAULT_COLUMN_KEYS.has(key)) return
    setVisibleColumnKeys((prev) => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const expenseColumnsWithActions = useMemo(
    () => [
      {
        title: t("accounting.details.columns.date"),
        dataIndex: "date",
        key: "date",
        ellipsis: true,
        width: 120,
        render: (date) => date || "-",
        onCell: (record) => ({ rowSpan: record._rowSpan, style: { verticalAlign: "top" } }),
        sorter: (a, b) => new Date(a.date || 0) - new Date(b.date || 0)
      },
      {
        title: t("accounting.details.columns.merchant_name"),
        dataIndex: "displayed_name",
        key: "displayed_name",
        ellipsis: true,
        render: (v) => (v || "-").replaceAll("\n", " ").trim(),
        onCell: (record) => ({ rowSpan: record._rowSpan, style: { verticalAlign: "top" } }),
        sorter: (a, b) => (a.displayed_name || "").localeCompare(b.displayed_name || "")
      },
      {
        title: t("accounting.details.columns.item_name"),
        key: "itemName",
        ellipsis: true,
        onCell: (record) => (record._isLastLineItem ? {} : { style: { borderBottom: "none" } }),
        render: (_, record) => (record._lineItem?.name || "-").replaceAll("\n", " ").trim()
      },
      {
        title: t("accounting.details.columns.category"),
        key: "category",
        ellipsis: true,
        onCell: (record) => (record._isLastLineItem ? {} : { style: { borderBottom: "none" } }),
        render: (_, record) => record._lineItem?.category?.name || "-"
      },
      {
        title: t("accounting.details.columns.amount_paid"),
        key: "amountPaid",
        ellipsis: true,
        align: "right",
        onCell: (record) => (record._isLastLineItem ? {} : { style: { borderBottom: "none" } }),
        render: (_, record) => {
          const amt = record._lineItem?.amount
          if (amt == null) return <span>-</span>
          return <span style={{ color: "var(--color-error)" }}>{`-${formatCurrency(amt)}`}</span>
        }
      },
      {
        title: t("accounting.details.columns.total_amount"),
        key: "totalAmount",
        ellipsis: true,
        align: "right",
        width: 130,
        onCell: (record) => ({ rowSpan: record._rowSpan, style: { verticalAlign: "top" } }),
        render: (_, record) => (
          <span style={{ color: "var(--color-error)" }}>
            {`-${record.formatted_amount || formatCurrency(Math.abs(record.amount || 0))}`}
          </span>
        )
      },
      {
        key: "actions",
        width: 48,
        onCell: (record) => ({ rowSpan: record._rowSpan, style: { verticalAlign: "top" } }),
        render: (_, record) => (
          <Dropdown
            menu={{
              items: [
                { key: "edit", label: t("accounting.details.edit") },
                { key: "delete", label: t("accounting.details.delete"), danger: true }
              ],
              onClick: ({ key }) => {
                if (key === "edit") onEdit(record)
                if (key === "delete") onDelete(record)
              }
            }}
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        )
      }
    ],
    [t, onEdit, onDelete]
  )

  const allCols = useMemo(() => [...baseSalesColumns(t), ...optionalSalesColumns(t)], [t])

  const optionalCols = useMemo(() => allCols.filter((col) => !DEFAULT_COLUMN_KEYS.has(col.key)), [allCols])

  const toggleAllOptional = useCallback(
    (e) =>
      setVisibleColumnKeys((prev) => {
        const next = new Set(prev)
        optionalCols.forEach((col) => (e.target.checked ? next.add(col.key) : next.delete(col.key)))
        return next
      }),
    [optionalCols]
  )

  const addColumnsContent = useMemo(() => {
    const allOptionalVisible = optionalCols.every((col) => visibleColumnKeys.has(col.key))
    const someOptionalVisible = optionalCols.some((col) => visibleColumnKeys.has(col.key))

    return (
      <div style={{ minWidth: 200 }}>
        <Checkbox checked={allOptionalVisible} indeterminate={someOptionalVisible && !allOptionalVisible} onChange={toggleAllOptional}>
          {t("accounting.details.filters.all")}
        </Checkbox>
        <Divider style={{ margin: "8px 0" }} />
        {allCols.map((col) => (
          <div key={col.key} style={{ marginBottom: 6 }}>
            <Checkbox
              checked={visibleColumnKeys.has(col.key)}
              disabled={DEFAULT_COLUMN_KEYS.has(col.key)}
              onChange={(e) => toggleColumn(col.key, e.target.checked)}
            >
              {col.title}
            </Checkbox>
          </div>
        ))}
      </div>
    )
  }, [t, visibleColumnKeys, allCols, optionalCols, toggleAllOptional, toggleColumn])

  const columns = useMemo(() => {
    if (!isSales) return expenseColumnsWithActions
    const visible = allCols.filter((col) => visibleColumnKeys.has(col.key))
    const addCol = {
      key: "add-columns",
      width: 48,
      fixed: "right",
      title: (
        <Popover content={addColumnsContent} title={t("accounting.details.filters.add_columns")} trigger="click" placement="bottomRight">
          <Button type="text" icon={<PlusOutlined />} size="small" />
        </Popover>
      ),
      render: () => null
    }
    return [...visible, addCol]
  }, [isSales, visibleColumnKeys, addColumnsContent, allCols, expenseColumnsWithActions, t])

  return { columns }
}
